import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount, useChainId, useSwitchChain } from 'wagmi';
import { base, arbitrum, polygon } from 'wagmi/chains';
import { AlertTriangle, CheckCircle, Wallet, Loader2, Plus, ExternalLink, X } from 'lucide-react';
import { Button } from './ui/button';

const SUPPORTED_CHAINS = [
    {
        ...base,
        label: 'Base',
        gradient: 'from-[#0052FF] to-[#0066FF]',
        accent: '#0066FF',
        recommended: true,
        addParams: {
            chainId: '0x2105',
            chainName: 'Base',
            nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
            rpcUrls: ['https://mainnet.base.org'],
            blockExplorerUrls: ['https://basescan.org'],
        },
    },
    {
        ...arbitrum,
        label: 'Arbitrum',
        gradient: 'from-[#28A0F0] to-[#3F8AC0]',
        accent: '#28A0F0',
        addParams: {
            chainId: '0xa4b1',
            chainName: 'Arbitrum One',
            nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
            rpcUrls: ['https://arb1.arbitrum.io/rpc'],
            blockExplorerUrls: ['https://arbiscan.io'],
        },
    },
    {
        ...polygon,
        label: 'Polygon',
        gradient: 'from-[#8247E5] to-[#A06CFF]',
        accent: '#8247E5',
        addParams: {
            chainId: '0x89',
            chainName: 'Polygon Mainnet',
            nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
            rpcUrls: ['https://polygon-rpc.com'],
            blockExplorerUrls: ['https://polygonscan.com'],
        },
    },
];

const SUPPORTED_IDS = SUPPORTED_CHAINS.map(c => c.id);

export default function NetworkSwitchModal() {
    const { t } = useTranslation();
    const { isConnected } = useAccount();
    const chainId = useChainId();
    const { switchChainAsync } = useSwitchChain();

    const [open, setOpen] = useState(false);
    const [busyChain, setBusyChain] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    const [autoTried, setAutoTried] = useState(false);

    const isWrongNetwork = isConnected && chainId && !SUPPORTED_IDS.includes(chainId);

    // Auto-open on wrong network
    useEffect(() => {
        if (isWrongNetwork) {
            setOpen(true);
            setErrorCode(null);
        } else {
            setOpen(false);
            setAutoTried(false);
        }
    }, [isWrongNetwork]);

    // Auto-attempt switch to Base on first open
    const attemptSwitch = useCallback(async (target) => {
        if (!window.ethereum) {
            setErrorCode('NO_PROVIDER');
            return;
        }
        setBusyChain(target.id);
        setErrorCode(null);
        try {
            await switchChainAsync({ chainId: target.id });
            setBusyChain(null);
        } catch (err) {
            // 4902 = chain not added; 4001 = user rejected
            const code = err?.code ?? err?.cause?.code;
            if (code === 4902 || /Unrecognized chain ID/i.test(err?.message || '')) {
                // Try to add the chain to the wallet directly
                try {
                    await window.ethereum.request({
                        method: 'wallet_addEthereumChain',
                        params: [target.addParams],
                    });
                    setBusyChain(null);
                } catch (addErr) {
                    setErrorCode(addErr?.code === 4001 ? 'USER_REJECTED' : 'ADD_FAILED');
                    setBusyChain(null);
                }
            } else if (code === 4001) {
                setErrorCode('USER_REJECTED');
                setBusyChain(null);
            } else {
                setErrorCode('UNKNOWN');
                setBusyChain(null);
            }
        }
    }, [switchChainAsync]);

    useEffect(() => {
        if (open && !autoTried && !busyChain) {
            setAutoTried(true);
            const timer = setTimeout(() => attemptSwitch(SUPPORTED_CHAINS[0]), 600);
            return () => clearTimeout(timer);
        }
    }, [open, autoTried, busyChain, attemptSwitch]);

    if (!open) return null;

    const errorMsg = errorCode === 'NO_PROVIDER' ? t('netswitch.errNoProvider')
        : errorCode === 'USER_REJECTED' ? t('netswitch.errRejected')
        : errorCode === 'ADD_FAILED' ? t('netswitch.errAddFailed')
        : errorCode === 'UNKNOWN' ? t('netswitch.errUnknown')
        : null;

    return (
        <div
            className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
            data-testid="netswitch-overlay"
        >
            <div
                className="relative w-full sm:max-w-lg mx-auto bg-[#001A4D] sm:rounded-3xl rounded-t-3xl border-t-2 sm:border-2 border-red-500/40 shadow-2xl overflow-hidden"
                data-testid="netswitch-modal"
            >
                {/* Close (escape hatch) */}
                <button
                    onClick={() => setOpen(false)}
                    className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
                    aria-label={t('common.close')}
                    data-testid="netswitch-close"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="px-6 sm:px-8 pt-7 pb-2 text-center">
                    <div className="w-16 h-16 rounded-full mx-auto mb-4 bg-red-500/15 flex items-center justify-center">
                        <AlertTriangle className="w-8 h-8 text-red-400" />
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">{t('netswitch.title')}</h2>
                    <p className="text-white/70 text-sm sm:text-base leading-relaxed mb-5">
                        {t('netswitch.desc')}
                    </p>
                </div>

                {/* Chain list */}
                <div className="px-6 sm:px-8 pb-2 space-y-2">
                    {SUPPORTED_CHAINS.map((c) => {
                        const isBusy = busyChain === c.id;
                        return (
                            <button
                                key={c.id}
                                onClick={() => attemptSwitch(c)}
                                disabled={busyChain !== null}
                                className={`w-full flex items-center gap-3 p-3 sm:p-4 rounded-2xl border transition-all text-left
                                    ${isBusy ? 'border-[#FFD700] bg-[#FFD700]/10' : 'border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10'}
                                    disabled:opacity-50 disabled:cursor-not-allowed`}
                                data-testid={`netswitch-chain-${c.id}`}
                            >
                                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${c.gradient} flex items-center justify-center shrink-0`}>
                                    <Wallet className="w-5 h-5 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-white font-semibold">{c.label}</span>
                                        {c.recommended && (
                                            <span className="text-[10px] uppercase tracking-wider bg-[#FFD700]/20 text-[#FFD700] px-2 py-0.5 rounded-full font-bold">
                                                {t('netswitch.recommended')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-white/50 text-xs">{t('netswitch.chainId')} {c.id}</div>
                                </div>
                                {isBusy ? (
                                    <Loader2 className="w-5 h-5 text-[#FFD700] animate-spin" />
                                ) : (
                                    <CheckCircle className="w-5 h-5 text-white/30" />
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Error / help */}
                {errorMsg && (
                    <div className="mx-6 sm:mx-8 mt-3 p-3 rounded-xl bg-red-500/10 border border-red-500/30">
                        <p className="text-red-300 text-sm font-semibold mb-1 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" /> {errorMsg}
                        </p>
                        {errorCode === 'NO_PROVIDER' && (
                            <a
                                href="https://metamask.io/download/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#FFD700] text-xs hover:underline flex items-center gap-1 mt-1"
                            >
                                {t('netswitch.installMetamask')} <ExternalLink className="w-3 h-3" />
                            </a>
                        )}
                        {errorCode === 'ADD_FAILED' && (
                            <p className="text-white/60 text-xs mt-2">
                                {t('netswitch.manualAddHint')}{' '}
                                <a
                                    href="https://chainlist.org"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#FFD700] hover:underline inline-flex items-center gap-1"
                                >
                                    chainlist.org <ExternalLink className="w-3 h-3" />
                                </a>
                            </p>
                        )}
                    </div>
                )}

                {/* How-to footer */}
                <div className="px-6 sm:px-8 pt-4 pb-6 sm:pb-7 text-center">
                    <details className="text-left bg-white/5 rounded-xl p-3" data-testid="netswitch-howto">
                        <summary className="text-[#FFD700] text-xs font-semibold cursor-pointer flex items-center gap-2">
                            <Plus className="w-3.5 h-3.5" /> {t('netswitch.howAdd')}
                        </summary>
                        <ol className="text-white/70 text-xs mt-2 space-y-1 pl-4 list-decimal">
                            <li>{t('netswitch.step1')}</li>
                            <li>{t('netswitch.step2')}</li>
                            <li>{t('netswitch.step3')}</li>
                        </ol>
                    </details>
                </div>
            </div>
        </div>
    );
}
